import { TabBar, TabBarItem } from 'react-native-tab-view';
import { Colors, Flex, Typography, useScreenSize } from '@banhala/ui-kit';
import type {
  SceneRendererProps,
  NavigationState,
} from 'react-native-tab-view';
import type { LikeTabRoute } from '../LikeContents';
import { LikeFolderTabTooltip } from './LikeFolderTabTooltip';
import { useCodiTabBadge } from './useCodiTabBadge';

const TAB_BAR_HEIGHT = 44;

const TAB_TEST_ID: Record<string, string> = {
  goods: '상품탭',
  folder: '내서랍탭',
  market: '마켓탭',
  style: '코디탭',
};

type LikeTabBarProps = SceneRendererProps & {
  navigationState: NavigationState<LikeTabRoute>;
  onTabClick?: (index: number) => void;
};

export const LikeTabBar = ({ onTabClick, ...props }: LikeTabBarProps) => {
  const { navigationState } = props;
  const currentRoute = navigationState.routes[navigationState.index];
  const { width: screenWidth } = useScreenSize();
  const tabWidth = screenWidth / navigationState.routes.length;
  const folderTabIndex = navigationState.routes.findIndex(
    route => route.id === 'folder',
  );
  const showCodiBadge = useCodiTabBadge(currentRoute?.id === 'style');

  return (
    <Flex alignSelf="center" width={screenWidth} position="relative" zIndex={1}>
      <TabBar
        {...props}
        onTabPress={({ route }) => {
          const nextIndex = navigationState.routes.indexOf(route);
          onTabClick?.(nextIndex);
        }}
        style={{
          height: TAB_BAR_HEIGHT,
          backgroundColor: Colors.bg_always_white,
          borderBottomWidth: 1,
          borderBottomColor: Colors.border_tertiary,
          elevation: 0,
          shadowOpacity: 0,
          width: screenWidth,
        }}
        tabStyle={{
          width: tabWidth,
        }}
        scrollEnabled={false}
        renderTabBarItem={tabItemProps => {
          const render = tabItemProps.route.render;
          return (
            <TabBarItem
              {...tabItemProps}
              key={tabItemProps.route.key}
              testID={TAB_TEST_ID[tabItemProps.route.id]}
              activeColor={render.selected_color}
              inactiveColor={render.unselected_color}
              label={({ route, focused, color }) => (
                <Flex>
                  <Typography
                    numberOfLines={1}
                    type={focused ? 'subtitle1.5' : 'body1.5'}
                    rawColor={color}
                  >
                    {route.name}
                  </Typography>
                  {route.id === 'style' && showCodiBadge && <NewBadge />}
                </Flex>
              )}
            />
          );
        }}
        indicatorStyle={{
          height: 2,
          backgroundColor:
            currentRoute?.render.selected_color ?? Colors.content_primary,
        }}
      />
      {folderTabIndex >= 0 && (
        <LikeFolderTabTooltip
          anchorCenterX={folderTabIndex * tabWidth + tabWidth / 2}
          anchorBottomY={TAB_BAR_HEIGHT}
        />
      )}
    </Flex>
  );
};

const NewBadge = () => (
  <Flex
    position={'absolute'}
    top={-1}
    right={-8}
    width={4}
    height={4}
    borderRadius={2}
    backgroundColor={'bg_pink_primary'}
  />
);
